"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
};

export default function ComingSoonClient({ until }: { until?: string }) {
  const launchAt = useMemo(() => {
    const fromQuery = until ? new Date(until) : null;
    const fallback = new Date();
    fallback.setMonth(fallback.getMonth() + 1);
    fallback.setHours(0, 0, 0, 0);
    return fromQuery && !isNaN(fromQuery.getTime()) ? fromQuery : fallback;
  }, [until]);

  const getLeft = (): TimeLeft => {
    const now = Date.now();
    const totalMs = Math.max(0, launchAt.getTime() - now);
    const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((totalMs / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((totalMs / (1000 * 60)) % 60);
    const seconds = Math.floor((totalMs / 1000) % 60);
    return { days, hours, minutes, seconds, totalMs };
  };

  const [left, setLeft] = useState<TimeLeft>(getLeft());
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const id = setInterval(() => setLeft(getLeft()), 1000);
    return () => clearInterval(id);
  }, [launchAt]);

  const onNotify = async (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: POST to /api/notify
    setSubmitted(true);
  };

  return (
    <main className="relative min-h-[100svh] mt-20 bg-gradient-to-b from-[#151A1F] to-[#0E1317] text-white overflow-hidden">
      {/* bg hero image tint */}
      <div className="absolute inset-0 -z-10 bg-[url('/imgs/menu-hero-bg.png')] bg-cover bg-center opacity-[0.08]" />

      {/* decorative shapes */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={116}
        height={310}
        viewBox="0 0 116 310"
        fill="none"
        className="hidden xl:block absolute top-20 left-0"
      >
        <circle
          cx="-38.6255"
          cy="154.962"
          r="154.356"
          stroke="#F0DC9C"
          strokeOpacity="0.5"
          strokeWidth="0.348901"
        />
      </svg>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={260}
        height={310}
        viewBox="0 0 260 310"
        fill="none"
        className="hidden xl:block absolute top-[350px] right-0"
      >
        <circle
          cx="155.507"
          cy="154.905"
          r="154.356"
          stroke="#F0DC9C"
          strokeOpacity="0.5"
          strokeWidth="0.348901"
        />
      </svg>

      <div className="hidden xl:block absolute bottom-1/2 left-0">
        <Image
          src="/imgs/shape-flower.png"
          alt="shape"
          width={200}
          height={318}
          className="z-50"
        />
      </div>
      <div className="hidden xl:block absolute bottom-0 right-0">
        <Image
          src="/imgs/shape-flower-2.png"
          alt="shape"
          width={200}
          height={318}
          className="z-50"
        />
      </div>

      {/* center content */}
      <section className="px-6">
        <div className="max-w-[1568px] mx-auto py-16 sm:py-20 lg:py-28 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-old-lace/60 px-3 py-1.5 bg-black/30 backdrop-blur-md text-sm text-old-lace/80">
            <span className="inline-block size-2 rounded-full bg-[#D4AF37]" />
            We’re preparing something special
          </div>

          {/* Title */}
          <h1 className="mt-6 text-[34px] sm:text-5xl lg:text-6xl xl:text-[80px] leading-[0.9] font-young-serif">
            Coming <span className="text-[#D4AF37]">Soon</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto text-sm sm:text-base">
            Freshly crafted experiences are on the way. Stay tuned for the grand
            reveal!
          </p>

          {/* Countdown */}
          <div className="mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <TimeBox value={left.days} label="Day" />
            <Dot />
            <TimeBox value={left.hours} label="Hour" />
            <Dot />
            <TimeBox value={left.minutes} label="Minute" />
            <Dot />
            <TimeBox value={left.seconds} label="Second" />
          </div>

          {/* Launch date line */}
          <p className="mt-4 text-xs sm:text-sm text-white/50">
            Target launch:{" "}
            <span className="text-old-lace/80">{launchAt.toUTCString()}</span>
          </p>

          {/* CTA buttons */}
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link
              href="/"
              className={cn(
                "px-4 py-2 rounded-md border transition text-sm",
                "border-white/20 text-white/80 hover:border-old-gold/60 hover:text-old-gold"
              )}
            >
              Back to Home
            </Link>
            <Link
              href="/menu"
              className={cn(
                "px-4 py-2 rounded-md border transition text-sm",
                "border-old-gold/60 text-old-gold hover:border-old-gold hover:bg-white/5"
              )}
            >
              Explore Menu
            </Link>
          </div>
        </div>
      </section>

      {/* background line art like menu page */}
      <div className="pointer-events-none absolute inset-x-0 top-1/4 -z-10 opacity-60">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="900"
          viewBox="0 0 1173 1139"
          fill="none"
        >
          <path
            d="M108.165 1.09589C108.165 1.09589 5.47254 138.995 133.087 234.802C260.702 330.609 427.098 203.751 637.816 106.991C848.535 10.2309 1184.43 -29.0792 1171.75 143.834C1159.08 316.748 283.781 242.649 154.018 341.041C24.2546 439.434 123.609 584.441 201.967 612.253C280.325 640.065 375.573 599.675 507.786 518.202C639.998 436.729 934.734 362.056 1023.49 474.269C1078.25 587.03 877.884 723.579 698.302 742.706C518.721 761.833 384.917 691 311.6 675.749C238.284 660.497 157.922 630.33 81.3134 698.156C4.70451 765.982 -69.996 945.205 123.609 1018.51C317.215 1091.81 795 705.515 971.393 752.27C1147.79 799.025 1159.79 1133.65 1018.21 1137.98"
            stroke="white"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    </main>
  );
}

function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="min-w-[82px] sm:min-w-[100px] px-3 py-3 sm:px-4 sm:py-4 rounded-xl border border-old-lace/60 bg-black/30 backdrop-blur-md shadow-[0px_0px_32px_rgba(212,175,55,0.15)]">
      <div className="text-[26px] sm:text-[34px] leading-none font-bold font-amiri text-spring-wood tracking-tight">
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-xs sm:text-sm text-old-lace/80 mt-1">
        {label}
        {value !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

function Dot() {
  return <div className="w-2 h-2 rounded-full bg-[#D4AF37]/80 mx-1 sm:mx-2" />;
}
